import { useRef, useState } from "react";
import { Upload, Loader2, Play as PlayIcon, Trash2 } from "lucide-react";
import { superUploadSound, superDeleteSound } from "../../../lib/api";
import { SOUND_SLOTS, type SoundSlot } from "../../../lib/audio";
import { useDialogs } from "../../../hooks/useDialogs";

// Restricciones del backend (apps/api/src/lib/r2.ts uploadAudio).
// Mantener en sync con MAX_AUDIO_BYTES del backend.
export const AUDIO_MAX_MB = 10;
export const AUDIO_ACCEPTED = "audio/mpeg,audio/wav,audio/ogg,audio/webm";

export function SoundUploader({
  slot,
  label,
  hint,
  currentUrl,
  onChanged,
}: {
  slot: string;
  /** Label visible. Si no se pasa, se intenta resolver desde SOUND_SLOTS
   *  (back-compat con los slots antiguos). */
  label?: string;
  /** Descripción debajo del label. Mismo fallback que `label`. */
  hint?: string;
  currentUrl: string | null;
  onChanged: () => void;
}) {
  const dialogs = useDialogs();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Fallback a SOUND_SLOTS por compat con los slots antiguos. Para los
  // slots nuevos (estilo-juego-evento) el caller pasa label + hint.
  const legacyInfo =
    slot in SOUND_SLOTS ? SOUND_SLOTS[slot as SoundSlot] : null;
  const displayLabel = label ?? legacyInfo?.label ?? slot;
  const displayHint = hint ?? legacyInfo?.hint ?? "";

  async function handleUpload(file: File) {
    setError(null);
    const acceptedTypes = AUDIO_ACCEPTED.split(",");
    if (!acceptedTypes.includes(file.type)) {
      setError(
        `Tipo no permitido: ${file.type || "desconocido"}. Solo MP3, WAV, OGG o WebM.`,
      );
      return;
    }
    if (file.size > AUDIO_MAX_MB * 1024 * 1024) {
      // Incluir nombre del file en el error — sin esto, si el browser
      // toma sin querer otro archivo (nombres similares truncados con
      // "..."), el user no se da cuenta que está rechazando otro.
      // El size se muestra en KB si <1 MB (más legible) para casos
      // donde el bug es de selección, no de tamaño real.
      const sizeKB = (file.size / 1024).toFixed(0);
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      const sizeLabel =
        file.size < 1024 * 1024 ? `${sizeKB} KB` : `${sizeMB} MB`;
      setError(
        `Archivo muy grande: "${file.name}" (${sizeLabel}). Máximo ${AUDIO_MAX_MB} MB.`,
      );
      return;
    }
    setUploading(true);
    try {
      await superUploadSound(file, slot);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete() {
    const ok = await dialogs.confirm({
      title: "Quitar sonido",
      message: `¿Quitar el sonido de "${displayLabel}"? Va a volver al sonido por defecto.`,
      confirmLabel: "Quitar",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await superDeleteSound(slot);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function handlePlay() {
    if (!currentUrl) return;
    if (!audioRef.current) audioRef.current = new Audio(currentUrl);
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  }

  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-3">
      {/* LAYOUT_ROTO fix: flex-wrap permite que la fila de botones baje
          ordenada en columnas angostas (en vez de romper el label a 2
          líneas y encimarse). El label lleva truncate; los botones
          shrink-0 para que conserven su tamaño. El código técnico del slot
          ya no se muestra: vive solo en el title del label (tooltip). */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span
            className="block truncate text-sm font-medium"
            title={slot}
          >
            {displayLabel}
          </span>
          {displayHint && (
            <p className="mt-0.5 text-xs opacity-60">{displayHint}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {currentUrl && (
            <button
              type="button"
              onClick={handlePlay}
              className="shrink-0 rounded p-1.5 hover:bg-white/10"
              title="Probar"
            >
              <PlayIcon className="h-4 w-4" />
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept={AUDIO_ACCEPTED}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
              e.target.value = "";
            }}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs hover:bg-white/20 disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {currentUrl ? "Cambiar" : "Subir"}
          </button>
          {currentUrl && (
            <button
              type="button"
              onClick={handleDelete}
              className="shrink-0 rounded p-1.5 text-red-300 hover:bg-red-500/20"
              title="Quitar (vuelve al sonido por defecto)"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {error && (
        <div className="mt-2 rounded-lg bg-red-500/15 px-2 py-1.5 text-xs text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}
